import { useCallback, useEffect, useRef, useState } from "react";
import {
    Loader2, Camera, ChevronRight, ChevronLeft, ImageIcon, AlertCircle,
    Store, User, Calendar, CheckCircle2, Circle, RotateCcw, X, ShieldAlert,
    Pencil, Trash2, Save,
} from "lucide-react";
import api, { API, tokenStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";
import { ResponsibleEmployees } from "./ResponsibleEmployees";

function toBackendUrl(relative) {
    if (!relative) return "";
    const t = tokenStore.getAccess();
    const abs = `${API.replace(/\/api$/, "")}${relative}`;
    return t ? `${abs}?auth=${encodeURIComponent(t)}` : abs;
}

const STATUS_ORDER = ["open", "in_progress", "closed"];

const STATUS_LABEL = {
    open: "qc_v_status_open",
    in_progress: "qc_v_status_in_progress",
    pending_approval: "qc_v_status_pending_approval",
    closed: "qc_v_status_closed",
    overdue: "qc_v_status_overdue",
};

/**
 * Violation detail + status transition dialog.
 *
 * Server responds with `can_manage` = whether the current caller may
 * transition status / upload after-photos on this violation. When `false`
 * the dialog switches to a read-only presentation.
 *
 * "Close" flow:
 *   1) Caller clicks "Close".
 *   2) If no after-photo yet -> server returns HTTP 400 with body
 *      `{code:"NEED_PROOF_OR_CONFIRM", ...}`. We catch that ONLY and pop a
 *      warning dialog. Any other 4xx surfaces as a normal toast.
 *   3) User can either "Upload after photo" (then click Close again) OR
 *      "Confirm close without proof" -> we resend with confirm_no_proof=true.
 */
export default function QCViolationDetailDialog({ violationId, onOpenChange, onChanged }) {
    const { me, isBranchManager, canApproveQc } = useAuth();
    const { t, lang } = useI18n();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [confirmClose, setConfirmClose] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [resolutionNote, setResolutionNote] = useState("");
    const [sendBackReason, setSendBackReason] = useState("");
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({ severity: "moderate", description: "", responsible_employees: [], deadline: "" });
    const origRespRef = useRef([]);
    const [employees, setEmployees] = useState([]);
    const [empAvailable, setEmpAvailable] = useState(null);
    const [empLoaded, setEmpLoaded] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const fileRef = useRef(null);

    const open = !!violationId;
    const canManage = !!data?.can_manage;
    const canEditDelete = !!data?.can_edit_delete;
    const effectiveStatus = data?.effective_status || "open";
    const rawStatus = data?.status || "open";

    const load = useCallback(async () => {
        if (!violationId) return;
        setEditing(false);
        setConfirmDelete(false);
        setLoading(true);
        try {
            const r = await api.get(`/qc-inspection/violations/${violationId}`);
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
            onOpenChange(false);
        } finally {
            setLoading(false);
        }
    }, [violationId, t, onOpenChange]);

    useEffect(() => { load(); }, [load]);

    // Load the HR roster for this violation's branch when the edit panel opens,
    // so the manager can re-assign to a real HR employee. Fail-soft: on any
    // error we mark HR unavailable and the field falls back to free-text.
    useEffect(() => {
        if (!editing || !data?.branch_id) { setEmployees([]); setEmpAvailable(null); setEmpLoaded(false); return; }
        let alive = true;
        setEmpLoaded(false);
        (async () => {
            try {
                const r = await api.get("/qc-inspection/employees", { params: { branch_id: data.branch_id } });
                if (!alive) return;
                setEmployees(r.data.employees || []);
                setEmpAvailable(!!r.data.available);
            } catch (e) {
                if (alive) { setEmployees([]); setEmpAvailable(false); }
            } finally {
                if (alive) setEmpLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, [editing, data?.branch_id]);

    // -------------------------------------------------- actions
    const changeStatus = async (nextStatus, opts = {}) => {
        if (!data || busy) return;
        setBusy(true);
        try {
            await api.post(`/qc-inspection/violations/${data.id}/status`, {
                status: nextStatus,
                confirm_no_proof: !!opts.confirmNoProof,
                note: opts.note || undefined,
            });
            toast.success(t("qc_v_status_updated"));
            setConfirmClose(false);
            setPendingAction(null);
            setResolutionNote("");
            setSendBackReason("");
            await load();
            onChanged && onChanged();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            // Machine-readable proof gate — pop the warning, remember the action.
            if (detail && typeof detail === "object" && detail.code === "NEED_PROOF_OR_CONFIRM") {
                setPendingAction({ status: nextStatus, note: opts.note });
                setConfirmClose(true);
            } else {
                toast.error(typeof detail === "string" ? detail : (detail?.message || t("error_generic")));
            }
        } finally {
            setBusy(false);
        }
    };

    // Branch manager submits the resolution (note + after-photo) for QC approval.
    const submitResolution = () => {
        const note = resolutionNote.trim();
        if (!note) { toast.error(t("qc_v_note_required")); return; }
        changeStatus("pending_approval", { note });
    };
    // QC manager approves the pending resolution (→ closed, final).
    const approve = () => changeStatus("closed", {});
    // QC manager sends the resolution back for rework (→ in_progress, reason required).
    const sendBack = () => {
        const reason = sendBackReason.trim();
        if (!reason) { toast.error(t("qc_v_reason_required")); return; }
        changeStatus("in_progress", { note: reason });
    };

    const uploadAfterPhoto = async (file) => {
        if (!data || !file || busy) return;
        setBusy(true);
        // ------ Watermark before upload (fail-closed) ------
        let watermarked;
        try {
            const { applyWatermark } = await import("@/lib/watermark");
            watermarked = await applyWatermark(file, {
                inspectorName: me?.user?.name,
                branchName: (lang === "ar" ? data.branch_name_ar : data.branch_name_en) || data.branch_name_ar || "",
                inspectionId: data.inspection_id || "",
            });
        } catch (e) {
            console.error("[QC watermark] failed", e);
            toast.error(t("qc_ins_watermark_failed"));
            setBusy(false);
            return;
        }
        try {
            const fd = new FormData();
            fd.append("file", watermarked);
            fd.append("photo_type", "after");
            fd.append("violation_id", data.id);
            await api.post(`/qc-inspection/inspections/${data.inspection_id}/photos`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(t("qc_v_after_uploaded"));
            await load();
            onChanged && onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setBusy(false);
        }
    };

    const onFileChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (file) uploadAfterPhoto(file);
    };

    // -------------------------------------------------- record-manager edit/delete
    const startEdit = () => {
        if (!data) return;
        const seed = Array.isArray(data.responsible_employees) ? data.responsible_employees : [];
        origRespRef.current = seed;
        setEditForm({
            severity: data.severity || "moderate",
            description: data.description || "",
            deadline: data.deadline || "",
            responsible_employees: seed,
        });
        setEditing(true);
    };

    const saveEdit = async () => {
        if (!data || busy) return;
        setBusy(true);
        try {
            const body = {
                severity: editForm.severity,
                description: editForm.description,
                deadline: editForm.deadline || "",
            };
            // Only touch the responsible list when the manager actually changed
            // it. A severity/description/deadline-only edit sends NOTHING here,
            // so the backend leaves the existing HR employee links fully intact
            // (fixes the employee_id-wiped-to-null bug).
            const orig = origRespRef.current || [];
            const now = editForm.responsible_employees || [];
            const sig = (rows) => JSON.stringify(rows.map((r) => [r.employee_id || null, r.full_name, r.source]));
            if (sig(orig) !== sig(now)) {
                body.responsible_employees = now;
            }
            await api.patch(`/qc-inspection/inspections/${data.inspection_id}/violations/${data.id}`, body);
            toast.success(t("qc_v_saved"));
            setEditing(false);
            await load();
            onChanged && onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setBusy(false);
        }
    };

    const deleteViolation = async () => {
        if (!data || busy) return;
        setBusy(true);
        try {
            await api.delete(`/qc-inspection/inspections/${data.inspection_id}/violations/${data.id}`);
            toast.success(t("qc_v_deleted"));
            setConfirmDelete(false);
            onOpenChange(false);
            onChanged && onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setBusy(false);
        }
    };

    // -------------------------------------------------- render
    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    data-testid={OPS.qcIns.violations.detailDialog}
                    className="max-w-3xl max-h-[92vh] overflow-y-auto scrollbar-thin"
                >
                    <DialogHeader>
                        <DialogTitle className="text-base">{t("qc_v_detail_title")}</DialogTitle>
                        <DialogDescription>{t("qc_v_detail_desc")}</DialogDescription>
                    </DialogHeader>

                    {loading || !data ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-[color:var(--brand-orange)]" />
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* ---------- summary ---------- */}
                            <section
                                data-testid={OPS.qcIns.violations.detailSummary}
                                className="rounded-lg surface-2 border border-[color:var(--border-soft)] p-3 space-y-2"
                            >
                                <div className="flex items-start gap-2">
                                    <AlertCircle className={`h-5 w-5 mt-0.5 shrink-0 ${
                                        data.severity === "critical"
                                            ? "text-[color:var(--danger)]"
                                            : data.severity === "moderate"
                                                ? "text-[color:var(--brand-orange)]"
                                                : "text-[color:var(--ink-muted)]"
                                    }`} />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-bold text-[color:var(--ink)]"><bdi>{data.item}</bdi></div>
                                        <div className="text-[11px] text-[color:var(--ink-muted)] mt-0.5">
                                            {data.section_key.replace(/_/g, " ")} · {t(`qc_ins_sev_${data.severity}`)}
                                        </div>
                                    </div>
                                    <Badge
                                        data-testid={OPS.qcIns.violations.detailStatus}
                                        variant="secondary"
                                        className={`${statusBadgeCls(effectiveStatus)} border-0 text-[10px] shrink-0`}
                                    >
                                        {t(STATUS_LABEL[effectiveStatus])}
                                    </Badge>
                                </div>

                                {data.description ? (
                                    <p className="text-xs text-[color:var(--ink)] leading-relaxed">
                                        <bdi>{data.description}</bdi>
                                    </p>
                                ) : null}

                                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[color:var(--ink-muted)]">
                                    <span className="inline-flex items-center gap-1">
                                        <Store className="h-3 w-3" />
                                        {lang === "ar" ? data.branch_name_ar : (data.branch_name_en || data.branch_name_ar)}
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <User className="h-3 w-3" />
                                        {data.inspector_name || "—"}
                                    </span>
                                    {(data.responsible_employees || []).length > 0 ? (
                                        <span className="inline-flex flex-wrap items-center gap-1">
                                            <span className="opacity-70">{t("qc_ins_responsible")}:</span>
                                            {(data.responsible_employees || []).map((r, i) => (
                                                <Badge
                                                    key={`${r.employee_id || r.full_name}-${i}`}
                                                    data-testid={OPS.qcIns.responsible.chip("detail", i)}
                                                    variant={r.employee_id ? "secondary" : "warning"}
                                                >
                                                    <bdi>{r.full_name}</bdi>
                                                    {!r.employee_id ? ` (${t("qc_ins_responsible_unverified")})` : ""}
                                                </Badge>
                                            ))}
                                        </span>
                                    ) : null}
                                    {data.deadline ? (
                                        <span className="inline-flex items-center gap-1">
                                            <Calendar className="h-3 w-3" />
                                            <bdi dir="ltr">{data.deadline}</bdi>
                                        </span>
                                    ) : null}
                                </div>

                                {data.closed_at ? (
                                    <div className="text-[11px] text-[color:var(--ink-muted)] border-t border-[color:var(--border-soft)] pt-2 space-y-0.5">
                                        <div>
                                            {t("qc_v_closed_by")}: <span className="text-[color:var(--ink)]">{data.closed_by_name || "—"}</span>
                                            {" · "}
                                            <bdi dir="ltr">{new Date(data.closed_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}</bdi>
                                        </div>
                                        {data.closed_without_proof ? (
                                            <div className="inline-flex items-center gap-1 text-[color:var(--danger)]">
                                                <ShieldAlert className="h-3 w-3" />
                                                {t("qc_v_closed_without_proof")}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </section>

                            {/* ---------- record-manager edit panel ---------- */}
                            {editing ? (
                                <section
                                    data-testid="qc-violation-edit-panel"
                                    className="rounded-lg surface border border-[color:var(--brand-orange)] p-3 space-y-3"
                                >
                                    <div className="flex items-center gap-2">
                                        <Pencil className="h-4 w-4 text-[color:var(--brand-orange)]" />
                                        <Label className="text-sm font-bold text-[color:var(--ink)]">{t("qc_v_edit_title")}</Label>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label className="text-xs text-[color:var(--ink-muted)]">{t("qc_v_f_severity")}</Label>
                                            <Select value={editForm.severity} onValueChange={(v) => setEditForm((f) => ({ ...f, severity: v }))}>
                                                <SelectTrigger data-testid={OPS.qcIns.violations.editSeverity}><SelectValue /></SelectTrigger>
                                                <SelectContent>
                                                    {["critical", "moderate", "minor"].map((s) => (
                                                        <SelectItem key={s} value={s}>{t(`qc_ins_sev_${s}`)}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label className="text-xs text-[color:var(--ink-muted)]">{t("qc_v_f_deadline")}</Label>
                                            <Input
                                                data-testid={OPS.qcIns.violations.editDeadline}
                                                type="date"
                                                value={editForm.deadline || ""}
                                                onChange={(e) => setEditForm((f) => ({ ...f, deadline: e.target.value }))}
                                            />
                                        </div>
                                        <div className="space-y-1.5 sm:col-span-2">
                                            <Label className="text-xs text-[color:var(--ink-muted)]">{t("qc_v_f_responsible")}</Label>
                                            <ResponsibleEmployees
                                                value={editForm.responsible_employees || []}
                                                employees={employees}
                                                employeesAvailable={!!empAvailable}
                                                employeesLoaded={empLoaded}
                                                canEdit
                                                onChange={(rows) => setEditForm((f) => ({ ...f, responsible_employees: rows }))}
                                                prefix="edit"
                                                t={t}
                                            />
                                        </div>
                                        <div className="space-y-1.5 sm:col-span-2">
                                            <Label className="text-xs text-[color:var(--ink-muted)]">{t("qc_v_f_description")}</Label>
                                            <Textarea
                                                data-testid={OPS.qcIns.violations.editDescription}
                                                rows={3}
                                                value={editForm.description}
                                                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2 justify-end">
                                        <Button size="sm" variant="ghost" data-testid={OPS.qcIns.violations.editCancel} onClick={() => setEditing(false)} disabled={busy}>
                                            {t("cancel")}
                                        </Button>
                                        <Button
                                            size="sm"
                                            data-testid={OPS.qcIns.violations.editSave}
                                            onClick={saveEdit}
                                            disabled={busy}
                                            className="gap-1.5 bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                                        >
                                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                            {t("save")}
                                        </Button>
                                    </div>
                                </section>
                            ) : null}

                            {/* ---------- status controls ---------- */}
                            {canManage ? (
                                <StatusControls
                                    rawStatus={rawStatus}
                                    effectiveStatus={effectiveStatus}
                                    busy={busy}
                                    hasAfterPhoto={data.photos_after.length > 0}
                                    isBranchManager={isBranchManager}
                                    canApproveQc={canApproveQc}
                                    data={data}
                                    resolutionNote={resolutionNote}
                                    setResolutionNote={setResolutionNote}
                                    sendBackReason={sendBackReason}
                                    setSendBackReason={setSendBackReason}
                                    onSubmitResolution={submitResolution}
                                    onApprove={approve}
                                    onSendBack={sendBack}
                                    onChange={changeStatus}
                                    onUpload={() => fileRef.current?.click()}
                                    t={t}
                                    lang={lang}
                                />
                            ) : (
                                <div
                                    data-testid={OPS.qcIns.violations.readonlyBanner}
                                    className="flex items-center gap-2 rounded-lg bg-[color:rgba(83,143,181,0.14)] px-3 py-2 text-xs text-[color:var(--brand-brown)]"
                                >
                                    <CheckCircle2 className="h-4 w-4" />
                                    {t("qc_v_readonly")}
                                </div>
                            )}

                            {/* ---------- before / after side-by-side ---------- */}
                            <PhotoCompare
                                before={data.photos_before}
                                after={data.photos_after}
                                lang={lang}
                                t={t}
                            />

                            {/* ---------- resolution history timeline ---------- */}
                            <ViolationTimeline
                                timeline={data.timeline}
                                createdAt={data.created_at}
                                lang={lang}
                                t={t}
                            />

                            {/* hidden file input for after-photo capture */}
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={onFileChange}
                                className="hidden"
                                data-testid={OPS.qcIns.violations.afterPhotoInput}
                            />
                        </div>
                    )}

                    <DialogFooter>
                        {canEditDelete && !editing ? (
                            <Button
                                variant="outline"
                                data-testid={OPS.qcIns.violations.editBtn}
                                onClick={startEdit}
                                disabled={busy}
                            >
                                <Pencil className="h-4 w-4 me-2" />
                                {t("qc_v_edit_btn")}
                            </Button>
                        ) : null}
                        {canEditDelete ? (
                            <Button
                                variant="destructive"
                                data-testid={OPS.qcIns.violations.deleteBtn}
                                onClick={() => setConfirmDelete(true)}
                                disabled={busy}
                            >
                                <Trash2 className="h-4 w-4 me-2" />
                                {t("qc_v_delete_btn")}
                            </Button>
                        ) : null}
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            <X className="h-4 w-4 me-2" />
                            {t("close")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* -------- close-without-proof confirm -------- */}
            <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("qc_v_close_no_proof_title")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("qc_v_close_no_proof_desc")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            data-testid={OPS.qcIns.violations.closeNoProofCancel}
                            disabled={busy}
                        >
                            {t("cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => changeStatus(pendingAction?.status || "closed", { confirmNoProof: true, note: pendingAction?.note })}
                            disabled={busy}
                            data-testid={OPS.qcIns.violations.closeNoProofConfirm}
                            className="bg-[color:var(--danger)] hover:bg-[color:var(--danger)]/90 text-white"
                        >
                            {busy ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                            {t("qc_v_close_no_proof_ok")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* -------- permanent delete confirm -------- */}
            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("qc_v_delete_confirm_title")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("qc_v_delete_confirm_desc")}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={OPS.qcIns.violations.deleteCancel} disabled={busy}>
                            {t("cancel")}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={deleteViolation}
                            disabled={busy}
                            data-testid={OPS.qcIns.violations.deleteConfirm}
                            className="bg-[color:var(--danger)] hover:bg-[color:var(--danger)]/90 text-white"
                        >
                            {busy ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}
                            {t("qc_v_delete_ok")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

// -------------------------------------------------- resolution timeline
const TL_META = {
    created: { i18n: "qc_tl_created", Icon: Circle, cls: "text-[color:var(--ink-muted)]" },
    started: { i18n: "qc_tl_started", Icon: ChevronRight, cls: "text-[color:var(--brand-brown)]" },
    submitted: { i18n: "qc_tl_submitted", Icon: CheckCircle2, cls: "text-[color:#6d28d9]" },
    sent_back: { i18n: "qc_tl_sent_back", Icon: RotateCcw, cls: "text-[color:var(--danger)]" },
    approved: { i18n: "qc_tl_approved", Icon: CheckCircle2, cls: "text-[color:var(--success)]" },
    closed: { i18n: "qc_tl_closed", Icon: CheckCircle2, cls: "text-[color:var(--success)]" },
    reopened: { i18n: "qc_tl_reopened", Icon: RotateCcw, cls: "text-[color:var(--brand-brown)]" },
};

function fmtTs(iso, lang) {
    if (!iso) return "";
    try {
        const d = new Date(iso.replace(" ", "T"));
        return d.toLocaleString(lang === "ar" ? "ar" : "en-GB", { dateStyle: "medium", timeStyle: "short" });
    } catch (e) { return iso; }
}

function ViolationTimeline({ timeline, createdAt, lang, t }) {
    const events = Array.isArray(timeline) ? timeline : [];
    // Synthesize a "created" entry at the very start so the story is complete.
    const entries = [
        ...(createdAt ? [{ event: "created", at: createdAt, by_name: null, note: null }] : []),
        ...events,
    ];
    if (entries.length === 0) return null;
    return (
        <section data-testid={OPS.qcIns.violations.timeline} className="rounded-lg surface border border-[color:var(--border-soft)] p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[color:var(--ink-muted)] mb-2">
                {t("qc_v_timeline_title")}
            </div>
            <ol className="relative space-y-3 ps-4 border-s border-[color:var(--border-soft)]">
                {entries.map((e, i) => {
                    const meta = TL_META[e.event] || { i18n: null, Icon: Circle, cls: "text-[color:var(--ink-muted)]" };
                    const Icon = meta.Icon;
                    return (
                        <li key={i} className="relative">
                            <span className={`absolute -start-[21px] top-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[color:var(--bg-surface)] ${meta.cls}`}>
                                <Icon className="h-3 w-3" />
                            </span>
                            <div className="text-[12px] font-semibold text-[color:var(--ink)] leading-snug">
                                {meta.i18n ? t(meta.i18n) : e.event}
                            </div>
                            <div className="text-[10px] text-[color:var(--ink-muted)] mt-0.5">
                                {fmtTs(e.at, lang)}{e.by_name ? ` · ${e.by_name}` : ""}
                            </div>
                            {e.note ? (
                                <p className="text-[11px] text-[color:var(--ink)] mt-1 rounded bg-[color:var(--muted)] px-2 py-1 leading-relaxed">
                                    <bdi>{e.note}</bdi>
                                </p>
                            ) : null}
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}

// -------------------------------------------------- status controls
function StatusControls({
    rawStatus, effectiveStatus, busy, hasAfterPhoto,
    isBranchManager, canApproveQc, data,
    resolutionNote, setResolutionNote, sendBackReason, setSendBackReason,
    onSubmitResolution, onApprove, onSendBack,
    onChange, onUpload, t, lang,
}) {
    const stepMeta = {
        open: { icon: Circle, label: "qc_v_status_open" },
        in_progress: { icon: ChevronRight, label: "qc_v_status_in_progress" },
        closed: { icon: CheckCircle2, label: "qc_v_status_closed" },
    };
    const currentIdx = STATUS_ORDER.indexOf(rawStatus);
    // A QC manager / super handles approvals; a plain branch manager does not.
    const bmOnly = isBranchManager && !canApproveQc;

    const AfterPhotoBar = (
        <div className="flex items-center justify-between gap-2 rounded-lg bg-[color:rgba(186,217,235,0.15)] px-3 py-2">
            <div className="text-[11px] text-[color:var(--ink-muted)]">
                {hasAfterPhoto ? t("qc_v_after_ready") : t("qc_v_after_hint")}
            </div>
            <Button
                variant="outline"
                size="sm"
                onClick={onUpload}
                disabled={busy}
                data-testid={OPS.qcIns.violations.uploadAfterBtn}
            >
                <Camera className="h-4 w-4 me-1" />
                {t("qc_v_add_after_photo")}
            </Button>
        </div>
    );

    // Show the last send-back reason (review note) when the issue is back with
    // the branch, so the branch manager sees WHY it was returned.
    const ReviewNoteBox = (data?.review_note && rawStatus !== "closed" && rawStatus !== "pending_approval") ? (
        <div data-testid={OPS.qcIns.violations.reviewNote}
            className="rounded-lg bg-[color:rgba(255,69,58,0.08)] border border-[color:rgba(255,69,58,0.2)] px-3 py-2 text-[11px]">
            <span className="font-semibold text-[color:var(--danger)]">{t("qc_v_sent_back_label")}</span>
            {data.reviewed_by_name ? <span className="text-[color:var(--ink-muted)]"> · {data.reviewed_by_name}</span> : null}
            <p className="text-[color:var(--ink)] mt-0.5"><bdi>{data.review_note}</bdi></p>
        </div>
    ) : null;

    // =================================================================
    //  PENDING APPROVAL — shown to everyone; approvers get action buttons
    // =================================================================
    if (rawStatus === "pending_approval") {
        return (
            <section
                data-testid={OPS.qcIns.violations.statusControls}
                className="rounded-lg surface border border-[color:var(--border-soft)] p-3 space-y-3"
            >
                <div data-testid={OPS.qcIns.violations.pendingBanner}
                    className="rounded-lg bg-[color:rgba(147,112,219,0.12)] border border-[color:rgba(147,112,219,0.25)] px-3 py-2 space-y-1">
                    <div className="text-xs font-bold text-[color:#6d28d9]">{t("qc_v_pending_banner")}</div>
                    {data?.resolution_note ? (
                        <p className="text-[11px] text-[color:var(--ink)] leading-relaxed">
                            <span className="opacity-70">{t("qc_v_resolution_note_label")}:</span>{" "}
                            <bdi>{data.resolution_note}</bdi>
                        </p>
                    ) : null}
                    {data?.resolution_submitted_by_name ? (
                        <p className="text-[10px] text-[color:var(--ink-muted)]">
                            {t("qc_v_resolution_by")}: {data.resolution_submitted_by_name}
                        </p>
                    ) : null}
                </div>

                {canApproveQc ? (
                    <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                            <Button
                                size="sm"
                                onClick={onApprove}
                                disabled={busy}
                                data-testid={OPS.qcIns.violations.approveBtn}
                                className="gap-1.5 bg-[color:var(--success)] hover:bg-[color:var(--success)]/90 text-white"
                            >
                                <CheckCircle2 className="h-4 w-4" />
                                {t("qc_v_approve_close")}
                            </Button>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-[11px] text-[color:var(--ink-muted)]">{t("qc_v_send_back_reason_label")}</Label>
                            <div className="flex gap-2">
                                <Input
                                    data-testid={OPS.qcIns.violations.sendBackReason}
                                    value={sendBackReason}
                                    onChange={(e) => setSendBackReason(e.target.value)}
                                    placeholder={t("qc_v_send_back_reason_ph")}
                                    disabled={busy}
                                />
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={onSendBack}
                                    disabled={busy}
                                    data-testid={OPS.qcIns.violations.sendBackBtn}
                                    className="gap-1.5 shrink-0"
                                >
                                    <RotateCcw className="h-4 w-4" />
                                    {t("qc_v_send_back")}
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="text-[11px] text-[color:var(--ink-muted)]">{t("qc_v_pending_wait")}</div>
                )}
                {AfterPhotoBar}
            </section>
        );
    }

    // =================================================================
    //  BRANCH-MANAGER path (open / in_progress) — submit for approval
    // =================================================================
    if (bmOnly) {
        return (
            <section
                data-testid={OPS.qcIns.violations.statusControls}
                className="rounded-lg surface border border-[color:var(--border-soft)] p-3 space-y-3"
            >
                <div className="flex items-center justify-between gap-2">
                    <Label className="text-[11px] font-bold uppercase tracking-wide text-[color:var(--ink-muted)]">
                        {t("qc_v_resolve_title")}
                    </Label>
                    {effectiveStatus === "overdue" ? (
                        <span className="text-[10px] text-[color:var(--danger)] font-semibold">{t("qc_v_overdue_now")}</span>
                    ) : null}
                </div>

                {ReviewNoteBox}

                {rawStatus === "open" ? (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onChange("in_progress")}
                        disabled={busy}
                        data-testid={OPS.qcIns.violations.setStatusBtn("in_progress")}
                        className="gap-1.5"
                    >
                        <ChevronRight className="h-4 w-4" />
                        {t("qc_v_start_working")}
                    </Button>
                ) : null}

                {AfterPhotoBar}

                <div className="space-y-1.5">
                    <Label className="text-[11px] text-[color:var(--ink-muted)]">{t("qc_v_resolution_note_label")}</Label>
                    <Textarea
                        data-testid={OPS.qcIns.violations.resolutionNote}
                        rows={2}
                        value={resolutionNote}
                        onChange={(e) => setResolutionNote(e.target.value)}
                        placeholder={t("qc_v_resolution_note_ph")}
                        disabled={busy}
                    />
                </div>
                <Button
                    size="sm"
                    onClick={onSubmitResolution}
                    disabled={busy}
                    data-testid={OPS.qcIns.violations.submitResolutionBtn}
                    className="w-full gap-1.5 bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    {t("qc_v_submit_resolution")}
                </Button>
                <p className="text-[10px] text-[color:var(--ink-muted)] text-center">{t("qc_v_pending_wait")}</p>
            </section>
        );
    }

    // =================================================================
    //  QC-MANAGER / SUPER / INSPECTOR path — direct stepper (unchanged)
    // =================================================================
    return (
        <section
            data-testid={OPS.qcIns.violations.statusControls}
            className="rounded-lg surface border border-[color:var(--border-soft)] p-3 space-y-3"
        >
            <div className="flex items-center justify-between gap-2">
                <Label className="text-[11px] font-bold uppercase tracking-wide text-[color:var(--ink-muted)]">
                    {t("qc_v_status_change")}
                </Label>
                {effectiveStatus === "overdue" ? (
                    <span className="text-[10px] text-[color:var(--danger)] font-semibold">
                        {t("qc_v_overdue_now")}
                    </span>
                ) : null}
            </div>

            {ReviewNoteBox}

            {/* Status stepper */}
            <div className="flex items-center gap-2" role="tablist">
                {STATUS_ORDER.map((s, i) => {
                    const active = i === currentIdx;
                    const Meta = stepMeta[s].icon;
                    return (
                        <div key={s} className="flex items-center gap-2">
                            <button
                                type="button"
                                data-testid={OPS.qcIns.violations.setStatusBtn(s)}
                                onClick={() => onChange(s)}
                                disabled={busy || active}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                                    active
                                        ? `${statusBadgeCls(s)} cursor-default`
                                        : "bg-[color:var(--muted)] text-[color:var(--ink-muted)] hover:bg-[color:var(--muted-hover,rgba(224,222,217,0.6))] hover:text-[color:var(--ink)]"
                                }`}
                            >
                                <Meta className="h-3.5 w-3.5" />
                                {t(stepMeta[s].label)}
                            </button>
                            {i < STATUS_ORDER.length - 1 ? (
                                lang === "ar"
                                    ? <ChevronLeft className="h-3 w-3 text-[color:var(--ink-muted)]" />
                                    : <ChevronRight className="h-3 w-3 text-[color:var(--ink-muted)]" />
                            ) : null}
                        </div>
                    );
                })}
                {rawStatus !== "open" ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onChange("open")}
                        disabled={busy}
                        data-testid={OPS.qcIns.violations.reopenBtn}
                        className="ms-auto text-[11px] text-[color:var(--ink-muted)]"
                    >
                        <RotateCcw className="h-3.5 w-3.5 me-1" />
                        {t("qc_v_reopen")}
                    </Button>
                ) : null}
            </div>

            {AfterPhotoBar}
        </section>
    );
}

// -------------------------------------------------- photo compare
function PhotoCompare({ before, after, lang, t }) {
    return (
        <section
            data-testid={OPS.qcIns.violations.photoCompare}
            className="grid grid-cols-1 md:grid-cols-2 gap-3"
        >
            <PhotoColumn label={t("qc_v_before")} photos={before} testId={OPS.qcIns.violations.photosBefore} lang={lang} t={t} />
            <PhotoColumn label={t("qc_v_after")} photos={after} testId={OPS.qcIns.violations.photosAfter} lang={lang} t={t} highlight />
        </section>
    );
}

function PhotoColumn({ label, photos, testId, highlight, t }) {
    return (
        <div data-testid={testId} className={`rounded-lg border p-3 ${
            highlight
                ? "border-[color:var(--success)]/40 bg-[color:rgba(46,160,67,0.06)]"
                : "border-[color:var(--border-soft)] bg-[color:rgba(224,222,217,0.15)]"
        }`}>
            <div className="flex items-center justify-between mb-2">
                <Label className="text-[11px] font-bold uppercase tracking-wide text-[color:var(--ink-muted)]">
                    {label}
                </Label>
                <span className="text-[10px] text-[color:var(--ink-muted)]">{photos.length}</span>
            </div>
            {photos.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-[11px] text-[color:var(--ink-muted)] border border-dashed border-[color:var(--border-soft)] rounded-lg">
                    <ImageIcon className="h-4 w-4 me-1" />
                    {t("qc_v_no_photos_yet")}
                </div>
            ) : (
                <div className="grid grid-cols-2 gap-2">
                    {photos.map((p) => (
                        <a
                            key={p.id}
                            href={toBackendUrl(p.url)}
                            target="_blank"
                            rel="noreferrer"
                            className="block rounded-lg overflow-hidden border border-[color:var(--border-soft)] bg-black/5"
                        >
                            <img
                                src={toBackendUrl(p.url)}
                                alt={p.original_filename || "photo"}
                                loading="lazy"
                                className="w-full h-24 object-cover"
                            />
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}

function statusBadgeCls(s) {
    return {
        open: "bg-[color:rgba(232,168,80,0.20)] text-[color:var(--brand-brown)]",
        in_progress: "bg-[color:rgba(83,143,181,0.22)] text-[color:var(--brand-brown)]",
        pending_approval: "bg-[color:rgba(147,112,219,0.18)] text-[color:#6d28d9]",
        closed: "bg-[color:rgba(46,160,67,0.14)] text-[color:var(--success)]",
        overdue: "bg-[color:rgba(255,69,58,0.14)] text-[color:var(--danger)]",
    }[s] || "bg-[color:rgba(224,222,217,0.5)] text-[color:var(--ink)]";
}
